import io                 from 'socket.io-client';
import { Socket }         from 'socket.io-client';
import { action }         from 'mobx';
import { makeObservable } from 'mobx';
import { observable }     from 'mobx';
import appStore           from '@/stores/appStore';
import { pickFreeColor }  from '@/TokamapEditor/helpers/randomColor';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import { API_ENV }        from '@/appConfig';
import currentEditorStore from '@/TokamapEditor/stores/currentEditorStore';
import { LngLat }         from '@tokamap/core';

export type User = SocketUser & { color: string; };

/** Un utilisateur présent quelque part dans le projet — `mapId` dit sur quelle carte. */
export type ProjectUser = User & { mapId: MapId; };

const socketIoApiEndpoints: Record<typeof API_ENV, string> = {
	local: 'https://dev-studio-socket-io-tokamap-com.staging.stonecode.io',
	dev: 'https://dev-studio-socket-io-tokamap-com.staging.stonecode.io',
	demo: 'https://demo-studio-socket-io-tokamap-com.staging.stonecode.io',
	preprod: 'https://preproduction-studio-socket-io-tokamap-com.staging.stonecode.io',
	prod: 'https://studio-socket-io.tokamap.com',
};

const socketIoApiEndpoint = socketIoApiEndpoints[API_ENV];

export default class EditorUserStore {
	readonly socket: Socket<SocketEvents> = io(socketIoApiEndpoint);

	users = observable.array<User>([]);

	// Présence à l'échelle du projet, toutes cartes confondues — distincte de `users`, qui ne connaît
	// que la carte ouverte (c'est elle qui pilote les curseurs et les sélections). Reste vide tant que
	// `studio-socket-io` n'émet pas `server:project-users` : les consommateurs retombent alors sur
	// `users`, soit le comportement d'avant.
	projectUsers = observable.array<ProjectUser>([]);

	// Couleur attribuée à chaque utilisateur, tenue ici et pas recalculée à chaque liste reçue : la
	// même personne doit garder sa couleur entre son avatar, sa pastille et son curseur, et ne pas en
	// changer parce que quelqu'un d'autre est arrivé avant elle dans la liste.
	private colorByUserId = new Map<UserId, string>();

	// Dernière position de curseur connue par utilisateur distant (voir `server:cursor-move` ci-dessous)
	// — pas observable, purement un cache technique pour pouvoir reconstruire un `RemoteCursor` complet
	// depuis `server:selection-change` seul (qui n'apporte pas de position). Volontairement en dehors de
	// `users` : la position n'a pas de sens pour l'UI (liste des utilisateurs connectés), seulement pour
	// `mapAdapter.setRemoteCursor`.
	private lastCursorPositionByUserId = new Map<UserId, LngLat>();

	// Forme actuellement sélectionnée par utilisateur distant (voir `server:selection-change`
	// ci-dessous) — même raison de ne pas vivre dans `users` que `lastCursorPositionByUserId`.
	private selectedShapeIdByUserId = new Map<UserId, string | null>();

	// `true` tant que la souris de cet utilisateur distant est hors de la carte (voir
	// `server:cursor-move` ci-dessous, `data.position === null`) — absent du Map ou `false` : souris
	// sur la carte (défaut). Même raison de ne pas vivre dans `users` que les 2 caches ci-dessus.
	private cursorLeftByUserId = new Map<UserId, boolean>();

	constructor() {
		makeObservable(this, {
			users: observable,
			setUsers: action,

			projectUsers: observable,
			setProjectUsers: action,
		});
	}

	setUsers(users: User[]) {
		this.users.replace(users);
	}

	setProjectUsers(projectUsers: ProjectUser[]) {
		this.projectUsers.replace(projectUsers);

		// Libère la couleur de ceux qui sont partis, pour ne pas épuiser la palette au fil d'une longue
		// session. Ceux encore présents sur la carte sont conservés même absents de la liste projet :
		// un serveur qui n'émet pas encore `server:project-users` la laisse vide.
		const present = new Set([...projectUsers.map(u => u.id), ...this.users.map(u => u.id)]);

		[...this.colorByUserId.keys()]
			.filter(userId => !present.has(userId))
			.forEach(userId => this.colorByUserId.delete(userId));
	}

	/** Couleur de cet utilisateur, attribuée à la première rencontre puis jamais changée. */
	private colorFor(userId: UserId): string {
		const known = this.colorByUserId.get(userId);

		if (known) return known;

		const color = pickFreeColor([...this.colorByUserId.values()]);
		this.colorByUserId.set(userId, color);

		return color;
	}

	restart() {
		this.stop();
		this.start();
	}

	start() {
		if (!currentEditorStore.map) return;

		this.socket.connect();

		// On avertit le serveur qu'un utilisateur se connecte sur la map
		this.socket.emit('client:join-map', {
			mapId: currentEditorStore.map.id,
			// Le projet en plus de la carte : c'est ce qui permet au serveur de dire qui travaille
			// ailleurs dans le même projet. Facultatif de bout en bout — un serveur qui ne le connaît
			// pas ignore le champ.
			projectId: currentProjectEditorStore.get?.project.id,
			user: { id: appStore.user.id, firstName: appStore.user.firstname, lastName: appStore.user.lastname },
		});

		this.socket.on('server:user-joined', data => {
			if (!this.users.map(u => u.id).includes(data.user.id)) {
				this.users.push({ ...data.user, color: this.colorFor(data.user.id) });
			}
		});

		this.socket.on('server:current-users', data => {
			this.setUsers(data.map(d => ({ ...d, color: this.colorFor(d.id) })));
		});

		this.socket.on('server:project-users', data => {
			this.setProjectUsers(data.map(d => ({ ...d, color: this.colorFor(d.id) })));
		});

		this.socket.on('server:user-left', data => {
			// Supprimer l'userId du tableau observable userIds
			this.setUsers(this.users.filter(user => user.id !== data.user.id));
			this.lastCursorPositionByUserId.delete(data.user.id);
			this.selectedShapeIdByUserId.delete(data.user.id);
			this.cursorLeftByUserId.delete(data.user.id);
		});

		this.socket.on('server:cursor-move', (data) => {
			// `position: null` : le curseur natif de cet utilisateur vient de quitter la carte (voir
			// `"map:pointer-left"`, index.tsx) — pas un évènement dédié, voir le commentaire de
			// `client:cursor-move` dans `socketIoTypes.d.ts` pour pourquoi. On garde volontairement
			// `lastCursorPositionByUserId` intacte (ne pas l'écraser avec `null`) : c'est la dernière
			// position RÉELLE connue, réutilisée telle quelle dès que son curseur reviendra sur la carte.
			if (data.position === null) {
				this.cursorLeftByUserId.set(data.userId, true);
			} else {
				this.lastCursorPositionByUserId.set(data.userId, data.position);
				this.cursorLeftByUserId.set(data.userId, false);
			}
			this.setRemoteCursorFor(data.userId);
		});

		this.socket.on('server:selection-change', (data) => {
			this.selectedShapeIdByUserId.set(data.userId, data.shapeId);
			this.setRemoteCursorFor(data.userId);
		});
	}

	// Reconstruit et transmet un `RemoteCursor` complet pour cet utilisateur à partir des caches
	// `lastCursorPositionByUserId`/`selectedShapeIdByUserId`/`cursorLeftByUserId` —
	// `mapAdapter.setRemoteCursor` remplace TOUJOURS l'état précédent en entier (voir son commentaire
	// dans `@tokamap/core`), donc chacun des 2 events `server:*` ci-dessus doit renvoyer les 3
	// informations, pas seulement celle qu'il vient de recevoir. No-op tant qu'aucune position n'a
	// jamais été reçue pour cet utilisateur (cas théorique : sélection reçue avant tout mouvement de
	// souris).
	private setRemoteCursorFor(userId: UserId) {
		const user = this.users.find(u => u.id === userId);
		const position = this.lastCursorPositionByUserId.get(userId);
		if (!user || !position) return;

		currentEditorStore.get.mapAdapter.setRemoteCursor({
			position,
			color: user.color,
			id: user.id,
			userName: `${user.firstName} ${user.lastName}`,
			selectedShapeId: this.selectedShapeIdByUserId.get(userId) ?? undefined,
			pointerVisible: !this.cursorLeftByUserId.get(userId),
		});
	}

	stop() {
		this.socket.disconnect();
		this.socket.off();
		this.projectUsers.clear();
		this.lastCursorPositionByUserId.clear();
		this.selectedShapeIdByUserId.clear();
		this.cursorLeftByUserId.clear();
	}
}